import React, { useEffect, useRef, useState } from 'react';
import { searchArtistForProvider, getArtistAlbumsForProvider, MUSIC_PROVIDER } from '../../services/musicProvider';
import { searchDeezerTracks } from '../../services/deezer';
import FavoritesService from '../../services/favorites';
import { claimPreviewPlayback, releasePreviewPlayback } from '../../services/previewAudioLock';
import { useApp } from '../../context/AppContext';
import { useLanguage } from '../../context/LanguageContext';
import LoginModal from './LoginModal';
import { LISTEN_PLATFORMS } from './ListenModal';
import '../../styles/artistCard.css';

/**
 * Card artista unica (mobile slide + desktop grid).
 * Modifica qui: entrambi i layout usano lo stesso componente.
 *
 * @param {'slide'|'grid'} variant
 */
const ArtistCard = ({
  artist,
  variant = 'grid',
  isActive = true,
  shouldLoad = true,
  shouldPreloadAlbums = true,
  pageType,
  position,
  onSlideReady,
  onBackToSearch,
  onBackToFirstPage,
  onOpenListenModal,
}) => {
  const { language } = useLanguage();
  const { favoriteArtistIds, refreshFavorites, isAuthenticated } = useApp();

  const cardRef = useRef(null);
  const audioRef = useRef(null);
  const previewIdRef = useRef(`preview-${Math.random().toString(36).slice(2)}`);
  const menuRef = useRef(null);
  const albumsRowRef = useRef(null);
  const albumsDragRef = useRef({ active: false, startX: 0, scrollLeft: 0, moved: false });
  const hasNotifiedReadyRef = useRef(false);
  const isLoadingRef = useRef(false);

  const isSlide = variant === 'slide';
  const [isVisible, setIsVisible] = useState(!isSlide);
  const [imageUrl, setImageUrl] = useState(
    artist?.images?.[0]?.url || artist?.image?.[0]?.['#text'] || artist?.image_url || null
  );
  const [providerData, setProviderData] = useState(null);
  const [albums, setAlbums] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [previewTime, setPreviewTime] = useState(0);
  const [previewDuration, setPreviewDuration] = useState(30);
  const [isPlayerLoading, setIsPlayerLoading] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);

  const name = artist?.name || '';
  const providerId =
    providerData?.id ||
    artist?.id ||
    artist?.deezer_id ||
    artist?.spotify_id ||
    artist?.spotifyId ||
    null;

  const canLoad = isSlide ? shouldLoad : isVisible;

  const notifyReady = () => {
    if (!hasNotifiedReadyRef.current && typeof onSlideReady === 'function') {
      onSlideReady(position);
      hasNotifiedReadyRef.current = true;
    }
  };

  // Grid: lazy via IntersectionObserver
  useEffect(() => {
    if (isSlide) return undefined;
    const el = cardRef.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [isSlide]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);

  useEffect(() => {
    hasNotifiedReadyRef.current = false;
    isLoadingRef.current = false;
    setProviderData(null);
    setAlbums([]);
    setTracks([]);
    setCurrentTrackIndex(0);
    const audio = audioRef.current;
    if (audio) audio.pause();
    setIsPlaying(false);
    releasePreviewPlayback(previewIdRef.current);
    setImageUrl(
      artist?.images?.[0]?.url || artist?.image?.[0]?.['#text'] || artist?.image_url || null
    );
  }, [artist?.name, artist?.images, artist?.image, artist?.image_url]);

  // Provider + immagine
  useEffect(() => {
    if (!canLoad || !name || isLoadingRef.current) return undefined;
    isLoadingRef.current = true;
    let cancelled = false;

    (async () => {
      try {
        const data = await searchArtistForProvider(
          name,
          artist?.spotifyId || artist?.spotify_id || null,
          artist?.genre || artist?.genres || null,
          artist?.origin || null,
          true
        );
        if (cancelled) return;
        if (!data) {
          notifyReady();
          return;
        }
        setProviderData(data);
        const url = data.images?.[0]?.url || null;
        if (url) setImageUrl(url);
        notifyReady();
      } catch (e) {
        notifyReady();
      } finally {
        isLoadingRef.current = false;
      }
    })();

    return () => {
      cancelled = true;
      isLoadingRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canLoad, name, artist?.spotifyId, artist?.spotify_id, artist?.genre, artist?.genres, artist?.origin]);

  // Album
  useEffect(() => {
    if (!shouldPreloadAlbums || !providerData?.id || albums.length > 0) return undefined;
    const delay = isSlide && !isActive ? 400 : 0;
    let cancelled = false;
    const timer = setTimeout(() => {
      getArtistAlbumsForProvider(providerData.id)
        .then((response) => {
          if (cancelled) return;
          const validAlbums = (response?.items || [])
            .filter((a) => a.album_type?.toLowerCase() === 'album' && a.id)
            .sort((a, b) => (b.release_date || '').localeCompare(a.release_date || ''))
            .slice(0, 6);
          setAlbums(validAlbums);
        })
        .catch(() => {});
    }, delay);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [shouldPreloadAlbums, isActive, isSlide, providerData?.id, albums.length]);

  // Preview Deezer (slide: solo se attiva; grid: quando visibile)
  useEffect(() => {
    const allowPreview = isSlide ? isActive : isVisible;
    if (!allowPreview || !name || tracks.length > 0) return undefined;
    let cancelled = false;
    setIsPlayerLoading(true);

    (async () => {
      try {
        const deezerResponse = await searchDeezerTracks(name, 20).catch(() => null);
        if (cancelled) return;
        const results = deezerResponse?.data || [];
        const normalized = name.toLowerCase();
        const filtered = results.filter((track) =>
          (track?.artist?.name || '').toLowerCase().includes(normalized)
        );
        setTracks((filtered.length > 0 ? filtered : results).slice(0, 8));
      } finally {
        if (!cancelled) setIsPlayerLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isSlide, isActive, isVisible, name, tracks.length]);

  // Preferiti
  useEffect(() => {
    const findFavorite = async () => {
      const rawId = artist?.spotify_id || artist?.spotifyId || providerData?.id;
      if (!rawId || !isAuthenticated) {
        setIsFavorite(false);
        return;
      }
      const rawIdString = String(rawId);
      const lookupKey =
        MUSIC_PROVIDER === 'deezer' && !rawIdString.startsWith('deezer:')
          ? `deezer:${rawIdString}`
          : rawIdString;
      try {
        const resolved = await FavoritesService.getArtistIdByUniqueKey(lookupKey);
        if (resolved) {
          setIsFavorite(Array.isArray(favoriteArtistIds) && favoriteArtistIds.includes(resolved));
        } else {
          setIsFavorite(false);
        }
      } catch (e) {
        setIsFavorite(false);
      }
    };
    findFavorite();
  }, [artist?.spotify_id, artist?.spotifyId, providerData?.id, favoriteArtistIds, isAuthenticated]);

  // Audio events
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;

    const onTime = () => setPreviewTime(audio.currentTime || 0);
    const onMeta = () => {
      const d = audio.duration;
      if (!Number.isNaN(d) && d) setPreviewDuration(d);
    };
    const onEnded = () => {
      setPreviewTime(0);
      if (tracks.length > 1) {
        setCurrentTrackIndex((prev) => (prev + 1) % tracks.length);
      } else {
        setIsPlaying(false);
      }
    };

    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('loadedmetadata', onMeta);
    audio.addEventListener('ended', onEnded);
    return () => {
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('loadedmetadata', onMeta);
      audio.removeEventListener('ended', onEnded);
    };
  }, [tracks.length]);

  const stopPreview = () => {
    const audio = audioRef.current;
    if (audio) audio.pause();
    setIsPlaying(false);
    releasePreviewPlayback(previewIdRef.current);
  };

  useEffect(() => {
    const audio = audioRef.current;
    const track = tracks[currentTrackIndex];
    if (!audio) return;
    if (!track?.preview) {
      stopPreview();
      return;
    }
    audio.pause();
    audio.src = track.preview;
    audio.load();
    audio.currentTime = 0;
    setPreviewTime(0);
    if (isPlaying) {
      claimPreviewPlayback(previewIdRef.current, stopPreview);
      audio.play().catch(() => {
        setIsPlaying(false);
        releasePreviewPlayback(previewIdRef.current);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTrackIndex, tracks]);

  // Pausa preview quando la slide non è attiva
  useEffect(() => {
    if (!isSlide || isActive) return;
    stopPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSlide, isActive]);

  useEffect(() => () => stopPreview(), []);

  const currentTrack = tracks[currentTrackIndex] || null;

  const ids = {
    deezerId: MUSIC_PROVIDER === 'deezer' ? providerId : artist?.deezer_id || null,
    spotifyId:
      MUSIC_PROVIDER === 'spotify'
        ? providerId
        : artist?.spotify_id || artist?.spotifyId || null,
  };

  const handleShare = () => {
    onOpenListenModal?.(name, null, ids, 'share');
  };

  const handleToggleFavorite = async () => {
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }
    if (!providerId) return;

    const wasFavorite = isFavorite;
    setIsFavorite(!wasFavorite);

    try {
      const payload =
        MUSIC_PROVIDER === 'deezer'
          ? {
              deezer_id: providerId,
              name,
              image_url: imageUrl,
              genres: providerData?.genres || [],
            }
          : {
              spotify_id: providerId,
              name,
              image_url: imageUrl,
              genres: providerData?.genres || [],
            };
      await FavoritesService.toggleFavorite(payload);
      refreshFavorites?.().catch(() => {});
    } catch (e) {
      setIsFavorite(wasFavorite);
    }
  };

  const togglePlay = async () => {
    const audio = audioRef.current;
    if (!audio || !currentTrack?.preview) return;
    if (isPlaying) {
      stopPreview();
      return;
    }
    claimPreviewPlayback(previewIdRef.current, stopPreview);
    try {
      await audio.play();
      setIsPlaying(true);
    } catch (e) {
      setIsPlaying(false);
      releasePreviewPlayback(previewIdRef.current);
    }
  };

  const onAlbumsPointerDown = (e) => {
    const row = albumsRowRef.current;
    if (!row) return;
    albumsDragRef.current = {
      active: true,
      startX: e.pageX,
      scrollLeft: row.scrollLeft,
      moved: false,
    };
    row.classList.add('is-dragging');
  };

  const onAlbumsPointerMove = (e) => {
    const row = albumsRowRef.current;
    const drag = albumsDragRef.current;
    if (!row || !drag.active) return;
    const delta = e.pageX - drag.startX;
    if (Math.abs(delta) > 4) drag.moved = true;
    row.scrollLeft = drag.scrollLeft - delta;
  };

  const onAlbumsPointerUp = () => {
    const row = albumsRowRef.current;
    albumsDragRef.current.active = false;
    row?.classList.remove('is-dragging');
  };

  useEffect(() => {
    const row = albumsRowRef.current;
    if (!row) return undefined;
    const onWheel = (e) => {
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
      row.scrollLeft += e.deltaY;
      e.preventDefault();
    };
    row.addEventListener('wheel', onWheel, { passive: false });
    return () => row.removeEventListener('wheel', onWheel);
  }, [albums.length]);

  const metaBits = [
    Array.isArray(artist?.genres)
      ? artist.genres.slice(0, 3).join(', ')
      : Array.isArray(providerData?.genres)
        ? providerData.genres.slice(0, 3).join(', ')
        : artist?.genre,
    artist?.origin,
  ].filter(Boolean);

  const showBack =
    (pageType === 'similar' && onBackToSearch) ||
    ((pageType === 'daily-discovery-artists' || pageType === 'random' || pageType === 'top-artists') &&
      onBackToFirstPage);

  const rootClass = `artist-card artist-card--${variant}${isActive ? ' is-active' : ''}`;

  return (
    <article className={rootClass} ref={cardRef}>
      <LoginModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} />

      <div className="artist-card__toolbar">
        <button
          type="button"
          className="artist-card__icon-btn"
          onClick={handleShare}
          aria-label={language === 'it' ? 'Condividi artista' : 'Share artist'}
        >
          <span className="material-icons">share</span>
        </button>
        <button
          type="button"
          className="artist-card__icon-btn"
          onClick={handleToggleFavorite}
          aria-label={
            language === 'it'
              ? isFavorite
                ? 'Rimuovi dai preferiti'
                : 'Aggiungi ai preferiti'
              : isFavorite
                ? 'Remove from favorites'
                : 'Add to favorites'
          }
        >
          <span className="material-icons" style={{ color: isFavorite ? '#FFD700' : undefined }}>
            {isFavorite ? 'star' : 'star_border'}
          </span>
        </button>
        {isSlide && showBack && (
          <button
            type="button"
            className="artist-card__icon-btn"
            onClick={onBackToSearch || onBackToFirstPage}
            aria-label={language === 'it' ? 'Torna indietro' : 'Back'}
          >
            <span className="material-icons">arrow_back</span>
          </button>
        )}
        <div className="artist-card__more" ref={menuRef}>
          <button
            type="button"
            className="artist-card__icon-btn"
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen((v) => !v);
            }}
            aria-label={language === 'it' ? 'Ascolta su' : 'Listen on'}
          >
            <span className="material-icons">more_vert</span>
          </button>
          {menuOpen && (
            <div className="artist-card__menu">
              {LISTEN_PLATFORMS.map((platform) => {
                const url = platform.getUrl(name, ids);
                return (
                  <a
                    key={platform.id}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setMenuOpen(false)}
                  >
                    <platform.Icon />
                    <span>{platform.name}</span>
                  </a>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Immagine a tutta card + layer scuro */}
      <div className="artist-card__photo" aria-hidden={!imageUrl}>
        {imageUrl ? (
          <img src={imageUrl} alt="" loading="lazy" />
        ) : (
          <div className="artist-card__placeholder" />
        )}
      </div>
      <div className="artist-card__scrim" />

      <div className="artist-card__content">
        <div className="artist-card__header">
          <h2 className="artist-card__name">{name || 'Unknown Artist'}</h2>
          {metaBits.length > 0 && (
            <p className="artist-card__meta">{metaBits.join(' · ')}</p>
          )}
        </div>

        {albums.length > 0 && (
          <div className="artist-card__albums">
            <h3>Top albums</h3>
            <div
              className="artist-card__albums-row"
              ref={albumsRowRef}
              onMouseDown={onAlbumsPointerDown}
              onMouseMove={onAlbumsPointerMove}
              onMouseUp={onAlbumsPointerUp}
              onMouseLeave={onAlbumsPointerUp}
            >
              {albums.map((album) => (
                <button
                  key={album.id}
                  type="button"
                  className="artist-card__album"
                  title={album.name}
                  onClick={(e) => {
                    if (albumsDragRef.current.moved) {
                      e.preventDefault();
                      albumsDragRef.current.moved = false;
                      return;
                    }
                    onOpenListenModal?.(
                      name,
                      album.name,
                      {
                        ...ids,
                        deezerAlbumId: MUSIC_PROVIDER === 'deezer' ? album.id : null,
                      },
                      'listen'
                    );
                  }}
                >
                  {album.images?.[0]?.url || album.cover_medium ? (
                    <img
                      src={album.images?.[0]?.url || album.cover_medium}
                      alt={album.name}
                      draggable={false}
                    />
                  ) : (
                    <div className="artist-card__album-ph" />
                  )}
                  <span>{album.name}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {currentTrack && (
          <div className="artist-card__player">
            <h3>Preview (30<span className="artist-card__preview-unit">s</span>)</h3>
            <div className="artist-card__track">
              <div className="artist-card__track-art">
                {currentTrack.album?.cover_medium ? (
                  <img src={currentTrack.album.cover_medium} alt="" />
                ) : (
                  <span className="material-icons">music_note</span>
                )}
              </div>
              <div className="artist-card__track-meta">
                <strong>{currentTrack.title}</strong>
                <span>{currentTrack.artist?.name || name}</span>
              </div>
              <div className="artist-card__track-controls">
                <button
                  type="button"
                  onClick={() =>
                    setCurrentTrackIndex((i) => (i - 1 + tracks.length) % tracks.length)
                  }
                  aria-label="Anteprima precedente"
                >
                  <span className="material-icons">skip_previous</span>
                </button>
                <button type="button" className="is-play" onClick={togglePlay}>
                  <span className="material-icons">{isPlaying ? 'pause' : 'play_arrow'}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setCurrentTrackIndex((i) => (i + 1) % tracks.length)}
                  aria-label="Anteprima successiva"
                >
                  <span className="material-icons">skip_next</span>
                </button>
              </div>
            </div>
          </div>
        )}
        <audio ref={audioRef} preload="none" />
      </div>
    </article>
  );
};

export default ArtistCard;
